import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Paperclip, Send, Loader2, X, MessageSquare, Plus, Image as ImageIcon, ChevronDown, Download, LayoutGrid, Minus,
} from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { SLOT_SCREEN_ORDER } from '@core/types/slot-screen-generator.types';
import { DEFAULT_SCREEN_CONFIGS } from '../../constants/slot-screen-defaults';
import { sendChatMessageStream } from '../../services/slot-screen-generator';
import { saveIdeaMessageImages } from '../../services/slot-screen-generator';
import { IdeaChatMessage } from './IdeaChatMessage';
import type {
  IdeaChatMessage as IdeaChatMessageType,
  IdeaChatModelId,
  IdeaChatMode,
} from '@core/types/slot-screen-generator.types';

const generateId = () => Math.random().toString(36).substring(2, 11);

const MODELS: Array<{ id: IdeaChatModelId; label: string; desc: string }> = [
  { id: 'gemini-3-flash-preview', label: 'Flash', desc: 'Answers quickly' },
  { id: 'gemini-3.1-pro-preview', label: 'Pro', desc: 'Advanced reasoning' },
];

export function IdeaChatMain() {
  const activeSessionId = useSlotScreenGeneratorStore(s => s.ideaCanvas.activeSessionId);
  const sessions = useSlotScreenGeneratorStore(s => s.ideaCanvas.sessions);
  const isGenerating = useSlotScreenGeneratorStore(s => s.ideaCanvas.isGenerating);
  const defaultModelId = useSlotScreenGeneratorStore(s => s.ideaCanvas.defaultModelId);
  const screens = useSlotScreenGeneratorStore(s => s.screens);

  const createSession = useSlotScreenGeneratorStore(s => s.createIdeaSession);
  const addMessage = useSlotScreenGeneratorStore(s => s.addIdeaMessage);
  const appendText = useSlotScreenGeneratorStore(s => s.appendIdeaMessageText);
  const appendThought = useSlotScreenGeneratorStore(s => s.appendIdeaMessageThought);
  const updateMessage = useSlotScreenGeneratorStore(s => s.updateIdeaMessage);
  const setGenerating = useSlotScreenGeneratorStore(s => s.setIdeaGenerating);
  const setModel = useSlotScreenGeneratorStore(s => s.setIdeaCanvasModel);
  const renameSession = useSlotScreenGeneratorStore(s => s.renameIdeaSession);
  const updateSavedPaths = useSlotScreenGeneratorStore(s => s.updateIdeaMessageSavedPaths);

  const session = sessions.find(s => s.id === activeSessionId) ?? null;

  const [inputText, setInputText] = useState('');
  const [attachedImages, setAttachedImages] = useState<string[]>([]);
  const [imageGenMode, setImageGenMode] = useState(false);
  const [imageCount, setImageCount] = useState(1);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showScreenPicker, setShowScreenPicker] = useState(false);
  const screenPickerRef = useRef<HTMLDivElement>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelPickerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages and streaming chunks
  const lastMsg = session?.messages[session.messages.length - 1];
  const lastMsgText = lastMsg?.text ?? '';
  const lastMsgThought = lastMsg?.thoughtText ?? '';
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session?.messages.length, lastMsgText, lastMsgThought]);

  // Close model picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) {
        setShowModelPicker(false);
      }
    };
    if (showModelPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showModelPicker]);

  // Close screen picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (screenPickerRef.current && !screenPickerRef.current.contains(e.target as Node)) {
        setShowScreenPicker(false);
      }
    };
    if (showScreenPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showScreenPicker]);

  // Get available screen images for the picker
  const availableScreenImages = SLOT_SCREEN_ORDER
    .map(id => {
      const screen = screens[id];
      const selectedResult = screen.selectedResultId
        ? screen.generatedResults.find(r => r.id === screen.selectedResultId && r.status === 'ready')
        : null;
      return selectedResult?.imageUrl ? { id, name: DEFAULT_SCREEN_CONFIGS[id]?.name ?? id, imageUrl: selectedResult.imageUrl } : null;
    })
    .filter((s): s is NonNullable<typeof s> => s !== null);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        setAttachedImages(prev => [...prev, URL.createObjectURL(file)]);
      }
    });
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) setAttachedImages(prev => [...prev, URL.createObjectURL(file)]);
      }
    }
  }, []);

  const removeAttachedImage = useCallback((index: number) => {
    setAttachedImages(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() && attachedImages.length === 0) return;
    if (isGenerating) return;

    // Ensure a session exists
    let sessionId = activeSessionId;
    if (!sessionId) {
      sessionId = createSession();
    }

    const mode: IdeaChatMode = imageGenMode ? 'create-image' : 'chat';
    const modelId = session?.modelId ?? defaultModelId;

    // Create user message
    const userMsg: IdeaChatMessageType = {
      id: generateId(),
      role: 'user',
      text: inputText.trim(),
      images: [...attachedImages],
      savedImagePaths: [],
      timestamp: Date.now(),
      mode,
    };
    addMessage(sessionId, userMsg);

    // Save user images to disk (fire-and-forget)
    if (userMsg.images.length > 0) {
      saveIdeaMessageImages(sessionId, userMsg.id, userMsg.images).then(paths => {
        if (paths.length > 0) updateSavedPaths(sessionId!, userMsg.id, paths);
      }).catch(() => {});
    }

    setInputText('');
    setAttachedImages([]);
    setGenerating(true);

    // Add empty model message immediately (will be filled by streaming chunks)
    const modelMsgId = generateId();
    addMessage(sessionId!, {
      id: modelMsgId,
      role: 'model',
      text: '',
      images: [],
      savedImagePaths: [],
      timestamp: Date.now(),
    });

    try {
      // Get full history for context (exclude the empty model msg we just added)
      const currentSession = useSlotScreenGeneratorStore.getState().ideaCanvas.sessions.find(
        s => s.id === sessionId,
      );
      const history = (currentSession?.messages ?? []).slice(0, -2); // Exclude user msg + empty model msg

      const response = await sendChatMessageStream(
        history,
        userMsg,
        modelId,
        mode,
        (delta) => appendText(sessionId!, modelMsgId, delta),
        (thoughtDelta) => appendThought(sessionId!, modelMsgId, thoughtDelta),
        imageGenMode ? imageCount : undefined,
      );

      // After streaming completes, update with images, thoughtSignature, and thoughtText
      if (response.images.length > 0 || response.thoughtSignature || response.thoughtText) {
        updateMessage(sessionId!, modelMsgId, {
          images: response.images,
          thoughtSignature: response.thoughtSignature,
          thoughtText: response.thoughtText,
        });
      }

      // Save model images to disk (fire-and-forget)
      if (response.images.length > 0) {
        saveIdeaMessageImages(sessionId!, modelMsgId, response.images).then(paths => {
          if (paths.length > 0) updateSavedPaths(sessionId!, modelMsgId, paths);
        }).catch(() => {});
      }
    } catch (err) {
      // Show error in the model message
      const errMsg = err instanceof Error ? err.message : String(err);
      updateMessage(sessionId!, modelMsgId, { text: `Error: ${errMsg}` });
    }

    setGenerating(false);
  }, [inputText, attachedImages, isGenerating, imageGenMode, imageCount, activeSessionId, session, defaultModelId, createSession, addMessage, appendText, appendThought, updateMessage, setGenerating, updateSavedPaths]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const startEditTitle = useCallback(() => {
    if (!session) return;
    setTitleDraft(session.title);
    setEditingTitle(true);
  }, [session]);

  const commitTitle = useCallback(() => {
    if (session && titleDraft.trim()) {
      renameSession(session.id, titleDraft.trim());
    }
    setEditingTitle(false);
  }, [session, titleDraft, renameSession]);

  const currentModel = MODELS.find(m => m.id === (session?.modelId ?? defaultModelId)) ?? MODELS[0];

  // ─── Empty state (no session) ──────────────────────────
  if (!session) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-zinc-600 gap-3"
           onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
        <MessageSquare size={48} strokeWidth={1} />
        <p className="text-sm text-zinc-400">Start a new conversation</p>
        <p className="text-xs text-zinc-600">Chat about slot game ideas, generate images, or get design feedback</p>
        <button
          onClick={() => createSession()}
          className="mt-2 flex items-center gap-2 px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
        >
          <Plus size={14} />
          New Chat
        </button>
      </div>
    );
  }

  // ─── Active session ────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col overflow-hidden relative"
         onDrop={handleDrop} onDragOver={e => e.preventDefault()}>

      {/* Header */}
      <div className="flex items-center px-4 py-2 border-b border-zinc-800 flex-shrink-0">
        {editingTitle ? (
          <input
            autoFocus
            value={titleDraft}
            onChange={e => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
            className="text-xs font-medium text-zinc-200 bg-zinc-800 border border-zinc-600 rounded px-2 py-1 outline-none"
          />
        ) : (
          <button
            onClick={startEditTitle}
            className="text-xs font-medium text-zinc-300 hover:text-zinc-100 truncate"
            title="Click to edit title"
          >
            {session.title}
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {session.messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-zinc-600 gap-2">
            <MessageSquare size={40} strokeWidth={1} />
            <p className="text-xs">Ask anything about your slot game</p>
          </div>
        )}
        {session.messages.map((msg, idx) => (
          <IdeaChatMessage
            key={msg.id}
            message={msg}
            onImageClick={setPreviewUrl}
            isStreaming={isGenerating && idx === session.messages.length - 1 && msg.role === 'model'}
          />
        ))}
        {/* Streaming cursor shown when generating and last message has no content yet */}
        {isGenerating && session.messages.length > 0 &&
          session.messages[session.messages.length - 1].role === 'model' &&
          !session.messages[session.messages.length - 1].text &&
          !session.messages[session.messages.length - 1].thoughtText && (
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Loader2 size={14} className="animate-spin" />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-zinc-800 p-3">
        {/* Attached images */}
        {attachedImages.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {attachedImages.map((url, i) => (
              <div key={i} className="relative w-12 h-12 rounded overflow-hidden group">
                <img src={url} alt={`Attached ${i+1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removeAttachedImage(i)}
                  className="absolute top-0 right-0 bg-black/60 rounded-bl p-0.5 opacity-0 group-hover:opacity-100"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Textarea */}
        <textarea
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={imageGenMode ? 'Describe the image to create...' : 'Ask about your slot game... (paste images with Ctrl+V)'}
          rows={2}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none focus:outline-none focus:border-zinc-600 mb-2"
        />

        {/* Bottom row: attach | image-gen toggle | model selector | send */}
        <div className="flex items-center gap-2">
          {/* Attach */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200"
            title="Attach images"
          >
            <Paperclip size={15} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => { handleFileSelect(e.target.files); e.target.value = ''; }}
          />

          {/* Screen image picker */}
          {availableScreenImages.length > 0 && (
            <div className="relative" ref={screenPickerRef}>
              <button
                onClick={() => setShowScreenPicker(prev => !prev)}
                className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-zinc-200 transition-colors"
                title="Attach screen image"
              >
                <LayoutGrid size={15} />
              </button>
              {showScreenPicker && (
                <div className="absolute bottom-full left-0 mb-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-[1000] p-2">
                  <div className="text-[10px] text-zinc-500 mb-1.5 px-1">Attach screen image</div>
                  <div className="flex gap-1.5">
                    {availableScreenImages.map(s => (
                      <button
                        key={s.id}
                        onClick={() => {
                          setAttachedImages(prev => [...prev, s.imageUrl]);
                          setShowScreenPicker(false);
                        }}
                        className="flex flex-col items-center gap-1 p-1 rounded hover:bg-zinc-700 transition-colors"
                        title={s.name}
                      >
                        <img src={s.imageUrl} alt={s.name} className="w-12 h-16 object-cover rounded" />
                        <span className="text-[9px] text-zinc-400 truncate w-12 text-center">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Chat / Image mode switcher */}
          <div className="flex items-center bg-zinc-800/50 border border-zinc-700/50 rounded-md p-0.5">
            <button
              onClick={() => setImageGenMode(false)}
              className={`flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] transition-colors ${
                !imageGenMode ? 'bg-zinc-700 text-zinc-200 font-medium' : 'text-zinc-500 hover:text-zinc-400'
              }`}
            >
              <MessageSquare size={11} />
              Chat
            </button>
            <button
              onClick={() => setImageGenMode(true)}
              className={`flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] transition-colors ${
                imageGenMode ? 'bg-zinc-700 text-zinc-200 font-medium' : 'text-zinc-500 hover:text-zinc-400'
              }`}
            >
              <ImageIcon size={11} />
              Image
            </button>
          </div>

          {/* Image count selector (visible only in image mode) */}
          {imageGenMode && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setImageCount(c => Math.max(1, c - 1))}
                disabled={imageCount <= 1}
                className="w-5 h-5 flex items-center justify-center rounded bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Minus size={10} />
              </button>
              <span className="text-[11px] text-zinc-400 font-medium w-6 text-center">{imageCount}/4</span>
              <button
                onClick={() => setImageCount(c => Math.min(4, c + 1))}
                disabled={imageCount >= 4}
                className="w-5 h-5 flex items-center justify-center rounded bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={10} />
              </button>
            </div>
          )}

          <div className="flex-1" />

          {/* Model selector */}
          <div className="relative" ref={modelPickerRef}>
            <button
              onClick={() => !imageGenMode && setShowModelPicker(prev => !prev)}
              disabled={imageGenMode}
              className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded transition-colors ${
                imageGenMode
                  ? 'text-zinc-500 cursor-not-allowed'
                  : 'text-zinc-300 hover:bg-zinc-800'
              }`}
            >
              {imageGenMode ? 'NB2' : currentModel.label}
              <ChevronDown size={11} />
            </button>

            {showModelPicker && (
              <div className="absolute bottom-full right-0 mb-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl py-1 z-[1000]">
                <div className="px-3 py-1.5 text-[10px] text-zinc-500 font-medium">Gemini 3</div>
                {MODELS.map(model => (
                  <button
                    key={model.id}
                    onClick={() => { setModel(model.id); setShowModelPicker(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-700 transition-colors ${
                      model.id === (session.modelId ?? defaultModelId) ? 'bg-zinc-700/50' : ''
                    }`}
                  >
                    <div>
                      <div className="text-xs text-zinc-200 font-medium">{model.label}</div>
                      <div className="text-[10px] text-zinc-500">{model.desc}</div>
                    </div>
                    {model.id === (session.modelId ?? defaultModelId) && (
                      <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={isGenerating || (!inputText.trim() && attachedImages.length === 0)}
            className="p-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-200 rounded-lg transition-colors"
            title="Send"
          >
            {isGenerating ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>

      {/* Image preview overlay — within this panel only */}
      {previewUrl && (
        <div
          className="absolute inset-0 z-50 bg-zinc-900/95 flex flex-col"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="flex items-center justify-end gap-2 px-4 py-2 flex-shrink-0">
            <a
              href={previewUrl}
              download={`image-${Date.now()}.png`}
              onClick={e => e.stopPropagation()}
              className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300 transition-colors"
              title="Download"
            >
              <Download size={16} />
            </a>
            <button
              onClick={() => setPreviewUrl(null)}
              className="p-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-zinc-300 transition-colors"
              title="Close (Escape)"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden p-4">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
